import { useMemo, useState, useCallback, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { products } from '@/mocks/products';
import { CartContext } from '@/context/CartContext';
import { WishlistContext } from '@/context/WishlistContext';
import ProductCard from '@/components/feature/ProductCard';
import ScrollReveal from '@/components/base/ScrollReveal';
import PullToRefresh from '@/components/base/PullToRefresh';
import ShopHeader from './components/ShopHeader';
import FilterSidebar from './components/FilterSidebar';
import ProductToolbar from './components/ProductToolbar';

interface FilterState {
  occasions: string[];
  styles: string[];
  categories: string[];
  priceMin: number;
  priceMax: number;
}

const DEFAULT_FILTERS: FilterState = {
  occasions: [],
  styles: [],
  categories: [],
  priceMin: 499,
  priceMax: 1899,
};

function parseSearchParams(params: URLSearchParams): FilterState {
  const occasions = params.get('occasion') ? [decodeURIComponent(params.get('occasion')!)] : [];
  const styleParam = params.get('style') ? decodeURIComponent(params.get('style')!) : '';
  const styles = styleParam ? [styleParam.replace(/\+/g, ' ')] : [];
  const categoryParam = params.get('category') ? decodeURIComponent(params.get('category')!) : '';
  const categories = categoryParam ? [categoryParam.replace(/\+/g, ' ').replace(/%26/g, '&')] : [];
  const priceMin = parseInt(params.get('priceMin') || '499', 10);
  const priceMax = parseInt(params.get('priceMax') || '1899', 10);

  return { occasions, styles, categories, priceMin, priceMax };
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters = useMemo(() => parseSearchParams(searchParams), []);
  const initialSort = searchParams.get('sort') || 'newest';
  const initialView = (searchParams.get('view') as 'grid' | 'list') || 'grid';

  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [sortBy, setSortBy] = useState(initialSort);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(initialView);

  const updateURL = useCallback(
    (newFilters: FilterState, newSort: string, newView: string) => {
      const params = new URLSearchParams();
      if (newFilters.occasions.length === 1) params.set('occasion', encodeURIComponent(newFilters.occasions[0]));
      if (newFilters.styles.length === 1) params.set('style', encodeURIComponent(newFilters.styles[0]));
      if (newFilters.categories.length === 1) params.set('category', encodeURIComponent(newFilters.categories[0]));
      if (newFilters.priceMin > 499) params.set('priceMin', newFilters.priceMin.toString());
      if (newFilters.priceMax < 1899) params.set('priceMax', newFilters.priceMax.toString());
      if (newSort !== 'newest') params.set('sort', newSort);
      if (newView !== 'grid') params.set('view', newView);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
      updateURL(newFilters, sortBy, viewMode);
    },
    [sortBy, viewMode, updateURL]
  );

  const handleClearAll = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    updateURL(DEFAULT_FILTERS, sortBy, viewMode);
  }, [sortBy, viewMode, updateURL]);

  const handleSortChange = useCallback(
    (newSort: string) => {
      setSortBy(newSort);
      updateURL(filters, newSort, viewMode);
    },
    [filters, viewMode, updateURL]
  );

  const handleViewModeChange = useCallback(
    (mode: 'grid' | 'list') => {
      setViewMode(mode);
      updateURL(filters, sortBy, mode);
    },
    [filters, sortBy, updateURL]
  );

  const filteredProducts = useMemo(() => {
    let result = products.filter((p) => {
      if (filters.occasions.length > 0 && !filters.occasions.includes(p.occasion)) return false;
      if (filters.styles.length > 0 && !filters.styles.includes(p.style)) return false;
      if (filters.categories.length > 0 && !filters.categories.includes(p.category)) return false;
      if (p.price < filters.priceMin || p.price > filters.priceMax) return false;
      return true;
    });

    switch (sortBy) {
      case 'price-low-high':
        result = [...result].sort((a, b) => a.price - b.price);
        break;
      case 'price-high-low':
        result = [...result].sort((a, b) => b.price - a.price);
        break;
      case 'bestselling':
        break;
      case 'newest':
      default:
        result = [...result].sort((a, b) => {
          if (a.isNew && !b.isNew) return -1;
          if (!a.isNew && b.isNew) return 1;
          return 0;
        });
        break;
    }

    return result;
  }, [filters, sortBy]);

  const activeFilterCount =
    filters.occasions.length +
    filters.styles.length +
    filters.categories.length +
    (filters.priceMin > 499 ? 1 : 0) +
    (filters.priceMax < 1899 ? 1 : 0);

  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = async () => {
    // Simulate a brief data refresh
    await new Promise((resolve) => setTimeout(resolve, 600));
    setRefreshKey((k) => k + 1);
  };

  return (
    <main>
      <ShopHeader activeFilters={activeFilterCount} />

      <PullToRefresh onRefresh={handleRefresh}>
        <section className="bg-background-50 py-8 md:py-12" key={refreshKey}>
        <div className="max-w-none mx-auto px-4 md:px-6">
          <div className="flex gap-8 lg:gap-10">
            {/* Sidebar */}
            <div className="hidden lg:block w-64 xl:w-72 flex-shrink-0">
              <FilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearAll={handleClearAll}
              />
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              {/* Mobile filter trigger */}
              <div className="lg:hidden">
                <FilterSidebar
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearAll={handleClearAll}
                />
              </div>

              {/* Toolbar */}
              <ProductToolbar
                resultCount={filteredProducts.length}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
              />

              {/* Product grid / list */}
              {filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 flex items-center justify-center mb-4">
                    <i className="ri-search-line text-3xl text-foreground-300"></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-700 mb-1">
                    No products found
                  </h3>
                  <p className="text-sm text-foreground-400 font-label max-w-sm">
                    Try adjusting your filters or clearing them to see the full collection.
                  </p>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={handleClearAll}
                      className="mt-4 px-5 py-2 rounded-lg font-label font-semibold text-xs bg-accent-500 text-primary-800 hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                /* Grid view */
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5"
                  data-product-shop
                >
                  {filteredProducts.map((product, idx) => (
                    <ScrollReveal key={product.id} delay={idx * 70}>
                      <ProductCard product={product} />
                    </ScrollReveal>
                  ))}
                </div>
              ) : (
                /* List view */
                <div className="flex flex-col gap-3 md:gap-4" data-product-shop>
                  {filteredProducts.map((product, idx) => (
                    <ScrollReveal key={product.id} delay={idx * 70}>
                      <ProductListItem product={product} />
                    </ScrollReveal>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      </PullToRefresh>
    </main>
  );
}

/* List view product item */
function ProductListItem({ product }: { product: typeof products[0] }) {
  const { addItem } = useContext(CartContext);
  const { isInWishlist, toggleWishlist } = useContext(WishlistContext);
  const [added, setAdded] = useState(false);
  const wished = isInWishlist(product.id);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const defaultVariant = product.variants
      ? `${product.variants[0].label}: ${product.variants[0].options[0]}`
      : undefined;
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      image: product.images[0],
      quantity: 1,
      variant: defaultVariant,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const discount =
    product.mrp > product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : 0;

  return (
    <Link
      to={`/product/${product.slug}`}
      className="flex gap-4 md:gap-5 p-3 md:p-4 bg-background-50 rounded-2xl border border-background-200/60 group hover:border-accent-300 transition-colors cursor-pointer"
    >
      {/* Image */}
      <div className="relative w-24 h-24 md:w-32 md:h-32 flex-shrink-0 rounded-xl overflow-hidden bg-secondary-100">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
        {product.isNew && (
          <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[9px] font-label font-semibold uppercase bg-accent-500 text-primary-800 rounded-full">
            New
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex flex-wrap gap-1.5 mb-1">
            <span className="text-[10px] font-label font-semibold uppercase tracking-wider text-accent-500">
              {product.style}
            </span>
            <span className="text-[10px] font-label text-foreground-400">·</span>
            <span className="text-[10px] font-label font-semibold uppercase tracking-wider text-accent-500">
              {product.occasion}
            </span>
          </div>
          <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-800 leading-snug mb-1">
            {product.name}
          </h3>
          <p className="text-xs text-foreground-500 line-clamp-2 font-label mb-2 hidden sm:block">
            {product.description}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-base md:text-lg font-heading font-bold text-primary-500">
              ₹{product.price}
            </span>
            {product.mrp > product.price && (
              <>
                <span className="text-xs text-foreground-400 line-through">₹{product.mrp}</span>
                <span className="text-[10px] font-label font-semibold text-accent-600">
                  {discount}% off
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleWishlist(product.id);
              }}
              className="hidden sm:flex w-8 h-8 items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`text-base ${
                    wished ? 'ri-heart-fill text-accent-500' : 'ri-heart-line text-foreground-400'
                  }`}
                ></i>
              </div>
            </button>
            <button
              onClick={handleAddToCart}
              className={`px-4 py-2 rounded-lg font-label font-semibold text-xs transition-all duration-200 cursor-pointer whitespace-nowrap ${
                added
                  ? 'bg-accent-500 text-primary-800'
                  : 'bg-primary-500 text-background-50 hover:bg-accent-500 hover:text-primary-800'
              }`}
            >
              {added ? 'Added ✓' : 'Add to Cart'}
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}